import { forwardRef, useCallback, useMemo, type ComponentProps } from 'react';
import { Icon, type IconName } from '../primitives/Icon';
import { ActivityIndicator, Pressable } from 'react-native';
import type { TextInput } from 'react-native';
import { XStack } from 'tamagui';
import { Input } from '../primitives/Input';
import { useThemeColors } from '../hooks/useThemeColors';
import {
  RowFocusContext,
  useRowFocusFieldContext,
  useRowFocusState,
} from '../hooks/useRowFocus';

export type SearchFieldSize = 'standard' | 'pill';

// Pinned literal — `as const` keeps the type as `'search'` (not `string`)
// so a future edit that misspells the value would shift the literal type
// and surface at the `as never` cast site instead of silently shipping
// a broken ARIA landmark.
const SEARCH_LANDMARK_ROLE = 'search' as const;

export interface SearchFieldProps
  extends Omit<
    ComponentProps<typeof Input>,
    'value' | 'onChangeText' | 'variant' | 'disabled' | 'size' | 'onSubmit'
  > {
  value: string;
  onChangeText: (next: string) => void;
  /** Defaults to `onChangeText('')`. */
  onClear?: (() => void) | undefined;
  /** Called on Enter / submit-editing — the implicit form-submit affordance. */
  onSubmit?: ((value: string) => void) | undefined;
  /** Leading icon — defaults to `search`. */
  icon?: IconName | undefined;
  /**
   * Layout preset:
   *  - `standard` (default): radius 16, ~44px tall — for forms, filter
   *    bars, lookup rows.
   *  - `pill`: radius 24, 48px tall — for hero searches that anchor a
   *    list (trips home, add-to-trip).
   */
  size?: SearchFieldSize | undefined;
  /** Override the surface tint if the SearchField sits on a non-surface background. */
  surfaceColor?: ComponentProps<typeof XStack>['backgroundColor'] | undefined;
  /** Greys out, blocks edit, drops the clear button. */
  disabled?: boolean | undefined;
  /** Replaces the clear icon with an ActivityIndicator while the search is
   *  pending. The input stays editable so users can refine the query. */
  loading?: boolean | undefined;
}

/**
 * Canonical search row. Owns the icon, the borderless `Input`, the clear
 * button, AND the focus border via `RowFocusContext` — so every search
 * surface (Review filter, place lookup on Stay / Journey, trips list,
 * trip add) lights up the same way. Before this primitive each screen
 * reinvented the row; `PlaceLookupRow` in particular shipped without a
 * focus provider, which is why the Stay / Journey searches never lit.
 *
 * Pass `onFocus` / `onBlur` to drive caller-side state (e.g. show a
 * predictions dropdown only while focused). The Input still reports
 * focus to this row's provider, so the cerulean ring is independent.
 *
 * Per ADR-0075: when nested inside a FormRow, picks up the outer
 * field's `error` / `disabled` state so the search row paints
 * `$borderError` instead of its own focus colour and announces the
 * error to screen readers.
 *
 * Phase 4 (ADR-0076):
 * - `disabled` / `loading` props — disabled blocks edit, loading swaps
 *   the clear icon for a spinner.
 * - Esc clears the value (web only); Enter calls `onSubmit(value)`.
 * - `role="search"` landmark for keyboard navigation.
 *
 * Visual idiom differs from MapPlaceSearch (1px border + shadow over a
 * map). That stays separate.
 */
// eslint-disable-next-line @typescript-eslint/no-shadow -- the inner function expression is deliberately named after the const so React DevTools and stack traces show the component name; it is not a real shadow.
export const SearchField = forwardRef<TextInput, SearchFieldProps>(function SearchField(
  {
    value,
    onChangeText,
    onClear,
    onSubmit,
    icon = 'search',
    size = 'standard',
    surfaceColor = '$surface',
    placeholder,
    disabled: disabledProp,
    loading = false,
    onKeyPress,
    onSubmitEditing,
    ...inputProps
  },
  ref,
) {
  const theme = useThemeColors();
  // Outer FormRow context wins where the explicit prop isn't set.
  const outerField = useRowFocusFieldContext();
  const hasError = Boolean(outerField.error);
  const isDisabled = disabledProp ?? Boolean(outerField.disabled);

  const { focused, reporter } = useRowFocusState();

  const layout =
    size === 'pill'
      ? { paddingHorizontal: 16, height: 48, borderRadius: 24, iconSize: 20 }
      : { paddingHorizontal: 14, height: 44, borderRadius: 18, iconSize: 18 };

  const handleClear = useCallback(() => {
    if (onClear) onClear();
    else onChangeText('');
  }, [onClear, onChangeText]);

  const handleKeyPress = useCallback(
    (e: Parameters<NonNullable<ComponentProps<typeof Input>['onKeyPress']>>[0]) => {
      const key = (e?.nativeEvent as { key?: string } | undefined)?.key ?? '';
      // Esc clears the search (web; native dismissal handles its own).
      if (key === 'Escape' && value) handleClear();
      onKeyPress?.(e);
    },
    [handleClear, onKeyPress, value],
  );

  const handleSubmitEditing = useCallback(
    (e: Parameters<NonNullable<ComponentProps<typeof Input>['onSubmitEditing']>>[0]) => {
      onSubmit?.(value);
      onSubmitEditing?.(e);
    },
    [onSubmit, onSubmitEditing, value],
  );

  const borderColor = hasError
    ? '$borderError'
    : focused
      ? '$borderFocus'
      : 'transparent';

  const showSpinner = loading;
  const showClear = !!value && !showSpinner && !isDisabled;

  // Memoize the context value so descendant consumers (the wrapped
  // Input + anything composed inside) don't re-render on every
  // keystroke — without this, every onChangeText flips a fresh object
  // identity into the Provider and invalidates every consumer.
  const rowContextValue = useMemo(
    () => ({
      reporter,
      ...(outerField.controlId ? { controlId: outerField.controlId } : null),
      ...(outerField.messageId ? { messageId: outerField.messageId } : null),
      ...(hasError ? { error: true as const } : null),
      ...(isDisabled ? { disabled: true as const } : null),
    }),
    [reporter, outerField.controlId, outerField.messageId, hasError, isDisabled],
  );

  return (
    <RowFocusContext.Provider value={rowContextValue}>
      <XStack
        // Search row is the screen-reader landmark for the search.
        // Tamagui's `Role` union omits 'search' (it has 'searchbox',
        // a different role); the typed literal below pins the spelling
        // so a typo would fail typecheck at the const, before `as never`
        // widens it for the Tamagui prop.
        role={SEARCH_LANDMARK_ROLE as never}
        alignItems="center"
        gap={10}
        paddingHorizontal={layout.paddingHorizontal}
        height={layout.height}
        backgroundColor={surfaceColor}
        borderRadius={layout.borderRadius}
        borderWidth={2}
        borderColor={borderColor}
        opacity={isDisabled ? 0.6 : 1}
      >
        <Icon name={icon} size={layout.iconSize} color={theme.textSubdued} />
        <Input
          ref={ref}
          value={value}
          onChangeText={onChangeText}
          placeholder={placeholder}
          flex={1}
          fontSize={14}
          editable={!isDisabled}
          returnKeyType={onSubmit ? 'search' : undefined}
          onSubmitEditing={handleSubmitEditing}
          onKeyPress={handleKeyPress}
          {...inputProps}
        />
        {showSpinner ? (
          <ActivityIndicator
            size="small"
            color={theme.textSubdued}
            accessibilityLabel="Searching"
          />
        ) : null}
        {showClear ? (
          <Pressable onPress={handleClear} hitSlop={12} accessibilityLabel="Clear search">
            <Icon name="cancel" size={18} color={theme.textSubdued} />
          </Pressable>
        ) : null}
      </XStack>
    </RowFocusContext.Provider>
  );
});
